<template>
  <div  class="w-full">
    <form @submit.prevent="handleSubmit">
      <div class="flex flex-column mb-4">
        <div class="md:col-6">
          <div class="border-round-sm">
            <label for="currentPassword" class="Label align-self-start mb-2">Current password</label>
            <IconField iconPosition="left" class="w-full">
              <InputIcon class="pi pi-lock" />
              <Password v-model="form.currentPassword" toggleMask placeholder="Enter current password here" size="small"
                variant="filled" class="w-full" id="currentPassword" autocomplete="current-password" />
            </IconField>
          </div>
        </div>
      </div>

      <div class="flex flex-column  mb-4">
        <div class="md:col-6">
          <div class="border-round-sm">
            <label for="newPassword" class="Label align-self-start mb-2">New password</label>
            <IconField iconPosition="left" class="w-full">
              <InputIcon class="pi pi-lock" />
              <Password v-model="form.newPassword" toggleMask placeholder="Enter new password here" size="small"
                variant="filled" class="w-full" :invalid="notValid" id="newPassword" autocomplete="new-password" />
            </IconField>
          </div>
        </div>
      </div>

      <div class="flex flex-column mb-4">
        <div class="md:col-6">
          <div class=" border-round-sm">
            <label for="confirmPassword" class="Label align-self-start mb-2">Confirm password</label>
            <IconField iconPosition="left" class="w-full">
              <InputIcon class="pi pi-lock" />
              <Password v-model="form.confirmPassword" toggleMask placeholder="Confirm password here" size="small"
                variant="filled" class="w-full" :invalid="notValid" id="confirmPassword" autocomplete="new-password" />
            </IconField>
          </div>
        </div>
      </div>

      <div class="flex flex-column  mb-4">
        <div class="col-12">
          <div class="border-round-sm">
            <Button type="submit" label="Update Password" :class="Submitbtn" :disabled="notValid"
              :loading="resendingCode" size="small" />
          </div>
        </div>
      </div>
    </form>

    <ConfirmDialog group="verify_otp">
      <template #container="{ message, rejectCallback }">
        <div class="p-4 text-center">
          <h2 class="font-bold text-lg mb-3">{{ message.header }}</h2>
          <p>We've sent a verification code to <br /><b>{{ props.profile.email }}</b>.</p>
          <p>Please enter it here.</p>
          <div class="card flex justify-center">
            <Divider />
            <InputOtp v-model="otp" variant="filled" :length="6" />
            <Divider />
          </div>
          <div class="mt-6 flex justify-center gap-4 justify-content-center">
            <Button label="Verify" @click="verifyCode" :class="Submitbtn" size="small" :disabled="otp.length < 6" />
            <Button label="Cancel" @click="rejectCallback" severity="danger" variant="text" size="small" />
          </div>
          <Divider />
          <div class="flex justify-center gap-4 mt-2 justify-content-center">
            <Button label="Resend Code" @click="sendVerificationCode(true)" severity="secondary" variant="text"
              size="small" :loading="resendingCode" />
            <Button label="I don't have access to this email" @click="rejectCallback" severity="contrast" variant="text"
              size="small" />
          </div>
        </div>
      </template>
    </ConfirmDialog>
  </div>
</template>

<script setup>
import { ref, computed, defineProps } from 'vue';
import { Button, Divider, IconField, InputIcon, InputOtp, Password } from 'primevue';
import ConfirmDialog from 'primevue/confirmdialog';
import { useConfirm } from "primevue/useconfirm";
import * as authApi from "@/api/auth";
import { notifyError, notifySuccess } from '@/utils/notification';

const props = defineProps({
  profile: { type: Object, required: true }
});

const form = ref({
  currentPassword: "",
  newPassword: "",
  confirmPassword: ""
});
const otp = ref("");
const resendingCode = ref(false);

const notValid = computed(() => {
  return (
    !form.value.currentPassword ||
    !form.value.newPassword ||
    form.value.newPassword !== form.value.confirmPassword
  );
});

const confirmOTP = useConfirm();

async function handleSubmit() {
  const valid = await verifyCurrentPassword();
  if (valid) await sendVerificationCode();
}

async function verifyCurrentPassword() {
  try {
    resendingCode.value = true;
    await authApi.verifyCurrentPassword({
      username: props.profile.email,
      password: form.value.currentPassword
    });
    return true;
  } catch {
    notifyError("Unauthorized!", "Current password is incorrect. Please try again.");
    return false;
  } finally {
    resendingCode.value = false;
  }
}

async function sendVerificationCode(resend = false) {
  try {
    resendingCode.value = true;
    await authApi.generatePasswordResetToken({ email: props.profile.email });
    notifySuccess("Check Your Inbox!", "We've sent a verification code to your email.");
    if (!resend) {
      confirmOTP.require({ group: 'verify_otp', header: 'Please confirm!' });
    }
  } catch {
    notifyError("Error", "Unable to send verification code. Please try again.");
  } finally {
    resendingCode.value = false;
  }
}

async function verifyCode() {
  try {
    await authApi.verifyResetToken({ email: props.profile.email, code: otp.value });
    otp.value = "";
    await updatePassword();
  } catch {
    notifyError("Invalid Code!", "Please ensure the verification code is correct.");
  }
}

async function updatePassword() {
  try {
    await authApi.resetPassword({ email: props.profile.email, password: form.value.newPassword });
    notifySuccess("Success", "Password has been reset successfully.");
    confirmOTP.close();
    form.value.currentPassword = "";
    form.value.newPassword = "";
    form.value.confirmPassword = "";
  } catch {
    notifyError("Oops!", "Unable to reset password. Please try again.");
  }
}
const Submitbtn = computed(() => {
  switch (props.profile.role) {
    case 'student':
      return 'orange-button';
    case 'tutor':
      return 'teal-button';
    case 'institute':
      return 'yellow-button';
    default:
      return 'teal-button';
  }
});
</script>
